import { css } from '@emotion/react';
import { Stack } from '@spark-web/stack';
import { ThemeProvider } from '@spark-web/theme';
import { useQueryState } from 'nuqs';
import { LoaderFunctionArgs, Outlet, useLoaderData } from 'react-router';

import { DatadogRum } from '~/components/datadog-rum';
import { PremarketplaceDialog } from '~/components/premarketplace/premarketplace-dialog';
import { usePartnerConfig } from '~/context/PartnerConfigContext';
import { PremarketplaceProvider } from '~/provider/PremarketplaceProvider';
import { StepProvider } from '~/provider/StepProvider';
import { getCategories } from '~/services/categories';
import { fetchEnterprisePartnerByChannel } from '~/services/enterprisePartner';
import { parseAsMarketplaceChannel, parseChannel } from '~/utils/channels';
import { toSparkTheme } from '~/utils/theme';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const currentUrl = new URL(request.url);
  const channelParam = currentUrl.searchParams.get('channel');

  const channel = parseChannel(channelParam) ?? 'brighte';

  const partnerResult = await fetchEnterprisePartnerByChannel(channel);
  const partner = partnerResult?.id ?? null;
  const channelFinancialProductId =
    partnerResult?.configuration?.channels?.find((c) => c.slug === channel)?.financialProductId ?? undefined;
  const categories = await getCategories(channelFinancialProductId, partner);

  const subCategories = categories.flatMap((category) => category.subCategories).filter((sc) => !!sc);

  return { categories: subCategories, partner };
};

function PremarketplaceParentLayout() {
  const { categories, partner } = useLoaderData<typeof loader>();

  const [channel] = useQueryState('channel', parseAsMarketplaceChannel.withDefault('brighte'));
  const config = usePartnerConfig();

  const theme = toSparkTheme(config?.configuration?.sparkThemeName);

  return (
    <ThemeProvider theme={theme}>
      <Stack height="full" css={css({ minHeight: '100vh' })}>
        <Stack as="main" paddingTop="small" tabIndex={-1} flex={1}>
          <PremarketplaceProvider channel={channel} categories={categories}>
            <DatadogRum />

            <StepProvider partner={partner ?? undefined}>
              <PremarketplaceDialog>
                <Outlet context={{ channel, partner: partner ?? undefined }} />
              </PremarketplaceDialog>
            </StepProvider>
          </PremarketplaceProvider>
        </Stack>
      </Stack>
    </ThemeProvider>
  );
}

export default PremarketplaceParentLayout;
